import { useMutation } from '@apollo/client'
import {
  CalculatePerDiemDocument,
  CalculatePerDiemMutation,
  CalculatePerDiemMutationVariables,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import { CalculationResult } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import {
  DayExclusion,
  Destination,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

interface CalculatePerDiemParams {
  employeeId: string
  startDate: Date | null
  endDate: Date | null
  from?: string
  destinations: Destination[]
  dayExclusions?: DayExclusion[]
}

export const useCalculatePerDiem = () => {
  const [calculateMutation, { loading }] = useMutation<
    CalculatePerDiemMutation,
    CalculatePerDiemMutationVariables
  >(CalculatePerDiemDocument)

  const calculate = useUnsafeCallback(
    async (params: CalculatePerDiemParams): Promise<Partial<CalculationResult>> => {
      if (!params.startDate || !params.endDate || params.destinations.length === 0) {
        return { totalAmountCents: 0, isCalculating: false }
      }

      const validDestinations = params.destinations.filter(
        (dest): dest is Destination & { arrivalDatetime: Date } =>
          Boolean(dest.locationData.country && dest.arrivalDatetime)
      )

      if (validDestinations.length === 0) {
        return { totalAmountCents: 0, isCalculating: false }
      }

      const destinations = validDestinations.map((dest) => ({
        arrivalDatetime: dest.arrivalDatetime.toISOString(),
        locationData: {
          country: dest.locationData.country,
          administrativeAreaLevel1: dest.locationData.administrativeAreaLevel1,
          administrativeAreaLevel2: dest.locationData.administrativeAreaLevel2,
          locality: dest.locationData.locality,
        },
      }))

      const dayExclusions = params.dayExclusions
        ?.map((exclusion) => {
          const excludedConcepts: Array<'breakfast' | 'lunch' | 'dinner' | 'accommodation'> = []
          if (exclusion.breakfastProvided) excludedConcepts.push('breakfast')
          if (exclusion.lunchProvided) excludedConcepts.push('lunch')
          if (exclusion.dinnerProvided) excludedConcepts.push('dinner')
          if (exclusion.accommodationProvided) excludedConcepts.push('accommodation')

          // Format Date to YYYY-MM-DD for API
          const date = exclusion.date
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(date.getDate()).padStart(2, '0')}`

          return {
            date: dateStr,
            excludedConcepts,
          }
        })
        .filter((e) => e.excludedConcepts.length > 0)

      const variables: CalculatePerDiemMutationVariables = {
        employeeId: params.employeeId,
        startDate: params.startDate.toISOString(),
        endDate: params.endDate.toISOString(),
        from: params.from,
        destinations,
        dayExclusions,
      }

      try {
        const result = await calculateMutation({
          variables,
        })

        const perDiem = result.data?.expensesMutations?.calculateExpensesPerDiem?.perDiem

        if (!perDiem) {
          return { totalAmountCents: 0, isCalculating: false, error: 'Failed to calculate' }
        }

        // amount is in cents (BigInt), convert to number
        const totalAmountCents = perDiem.amount ? Number(perDiem.amount) : 0

        return {
          totalAmountCents,
          currency: perDiem.currency ?? 'EUR',
          isCalculating: false,
          days: perDiem.days ?? null,
        }
      } catch {
        return { totalAmountCents: 0, isCalculating: false, error: 'Failed to calculate' }
      }
    },
    [calculateMutation]
  )

  return {
    calculate,
    loading,
  }
}
